import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Search, RotateCcw, RefreshCw, FileSpreadsheet, LayoutGrid, List, Users,
    Clock, ChevronDown, X, Edit, Check, AlertCircle, CalendarDays, UserRound, Info,
    Building2,
    BriefcaseBusiness,
    MapPin, BadgeCheck,
    ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';

import Breadcrumb, { BREADCRUMB_EXPORT_BTN } from '../../components/Breadcrumb/Breadcrumb';
import EmployeeSearch from '../../components/common/EmployeeSearch';
import {
    fetchAttendanceCard,
    fetchApprovedLeaves, fetchHolidays,
} from '../../store/slices/attendanceSlice';
import { fetchAllEmployees, fetchEmployeeByServiceNo, fetchEmployeeProfilePicUrl, } from '../../store/slices/employeeSlice';
import {
    fetchLeaveTypes, selectLeaveTypes,
} from '../../store/slices/leaveTypesSlice';
import {
    submitAdjustment, setAttendanceDate, setAdjustmentType,
    setAdjustedInTime, setAdjustedOutTime, setReason, resetForm,
    clearSearchError, clearSubmitError, selectIsSearching, selectSearchError,
    selectAdjustmentType, selectAdjustedInTime, selectAdjustedOutTime,
    selectReason, selectSubmitting, selectSubmitError, selectSubmitSuccess,
} from '../../store/slices/attendanceadjustmentSlice';
import { exportAttendanceDetailsToExcel } from '../../utils/attendanceDetailsExcel';

import { fetchAuthedBlobUrl } from '../../utils/authedFile';
import { getEmployeeProfilePicUrl } from '../../utils/employeeUtils';
import tenant from '../../config/tenant';

// ── shift-aware attendance status ───────────────
import {
    pad,
    toYMD, todayYMD,
    isSaturday, isSunday,
    extractShift, resolveShiftForDate, computeDayStatus,
    computeHours, computeOT, formatHoursMinutes,
} from '../../utils/attendanceStatus';

/* ── Sticky offsets — tune to your app shell ── */
const STICKY_TOP = -24;
const STICKY_BOTTOM = 0;

const FIELD =
    'w-full h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 text-gray-700 transition-shadow';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';

const ADJUSTMENT_TYPES = [
    'Incorrect IN Time', 'Incorrect OUT Time', 'Incorrect IN and OUT Times',
    'Missing IN Time', 'Missing OUT Time', 'Missing Full Day',
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => String(currentYear + 1 - i));
const MONTH_OPTIONS = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const STATUS_FILTERS = [
    { value: 'all', label: 'All Days' },
    { value: 'full-day', label: 'Full Day' },
    { value: 'late', label: 'Late' },
    { value: 'short-leave-morning', label: 'Morning Short Leave' },
    { value: 'short-leave-evening', label: 'Evening Short Leave' },
    { value: 'half-day-morning', label: 'Morning Half Day' },
    { value: 'half-day-evening', label: 'Evening Half Day' },
    { value: 'missing-punch', label: 'Missing Punch' },
    { value: 'leave', label: 'Leave' },
    // { value: 'sunday', label: 'Sunday' },
    { value: 'holiday', label: 'Holiday' },
    { value: 'no-record', label: 'No Record' },
];


function buildMonthDays(year, month) {
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const daysInMonth = new Date(y, m, 0).getDate();
    const list = [];
    for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(y, m - 1, d);
        const dow = date.getDay();
        list.push({
            day: d,
            dayName: DAY_NAMES_FULL[dow],
            dayShort: DAY_NAMES_SHORT[dow],
            isSunday: dow === 0,
            ymd: `${y}-${pad(m)}-${pad(d)}`,
        });
    }
    return list;
}

/* Solid hover backgrounds for sticky cells */
const rowBgHover = (r) => {
    switch (r.status) {
        case 'present': return 'group-hover:bg-gray-100';
        case 'late': return 'group-hover:bg-orange-100';
        case 'half-day': return 'group-hover:bg-indigo-100';
        case 'missing-punch': return 'group-hover:bg-fuchsia-100';
        case 'leave': return 'group-hover:bg-blue-200';
        case 'half-leave': return 'group-hover:bg-cyan-100';
        case 'short-leave': return 'group-hover:bg-sky-100';
        case 'holiday': return 'group-hover:bg-yellow-100';
        case 'no-record': return 'group-hover:bg-slate-100';
        default: return 'group-hover:bg-slate-50';
    }
};

/* ── Portal-based tooltip */
function InfoTip({ children }) {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);

    const handleEnter = () => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const tooltipW = 256;
        const pad = 8;
        const left = Math.min(
            Math.max(rect.left + rect.width / 2 - tooltipW / 2, pad),
            window.innerWidth - tooltipW - pad
        );
        setCoords({ top: rect.bottom + 8, left });
        setOpen(true);
    };
    const handleLeave = () => setOpen(false);

    return (
        <>
            <span
                ref={triggerRef}
                className="inline-flex items-center"
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
            >
                <Info size={11} className="text-slate-400 hover:text-slate-600 cursor-help" />
            </span>
            {open &&
                createPortal(
                    <span
                        role="tooltip"
                        className="pointer-events-none fixed z-[9999] w-64
                                   rounded-lg bg-slate-900 text-white text-[11px] leading-snug font-normal
                                   px-3 py-2 shadow-lg normal-case tracking-normal text-left"
                        style={{ top: coords.top, left: coords.left }}
                    >
                        {children}
                        <span
                            className="absolute w-2 h-2 bg-slate-900"
                            style={{ top: -4, left: '50%', transform: 'translateX(-50%) rotate(45deg)' }}
                        />
                    </span>,
                    document.body
                )}
        </>
    );
}

// ── TimePicker ───────────────────────────────────────────────
function TimePicker24h({ value, onChange, label, required = false, disabled = false }) {
    const timeInputRef = useRef(null);
    const displayValue = useMemo(() => (!value ? '' : value), [value]);
    const openTimePicker = () => { if (!disabled) timeInputRef.current?.showPicker?.(); };
    return (
        <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-700">
                {label} {required && '*'}
            </label>
            <div className="relative cursor-pointer" onClick={openTimePicker}>
                <input ref={timeInputRef} type="time"
                    className="absolute opacity-0 w-0 h-0 pointer-events-none"
                    value={value || ''} onChange={(e) => onChange(e.target.value)}
                    disabled={disabled} step="60" />
                <div className="relative">
                    <input type="text" readOnly disabled={disabled}
                        className={`w-full h-[34px] rounded-md border border-gray-200 px-3 pr-10 text-xs text-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'
                            }`}
                        value={displayValue} placeholder="HH:MM" />
                    <Clock size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
            </div>
        </div>
    );
}

/* Column definitions — Date + Day sticky, widths tuned for Hh MMm content */
const COLUMNS = [
    { key: 'ymd', label: 'Date', width: 130, align: 'left', sticky: true, left: 0 },
    { key: 'dayName', label: 'Day', width: 120, align: 'left', sticky: true, left: 130 },
    { key: 'status', label: 'Status', width: 150, align: 'center' },
    { key: 'inTime', label: 'In Time', width: 100, align: 'center' },
    { key: 'outTime', label: 'Out Time', width: 100, align: 'center' },
    {
        key: 'totalHours', label: 'Total Hours', width: 110, align: 'center',
        info: (
            <>
                <span className="font-semibold">Total Hours</span> = Normal Hours + Adjusted OT Hours.
                Shown as decimal hours (e.g. <span className="font-mono">7.77 h</span>).
            </>
        ),
    },
    {
        key: 'normalHours', label: 'Normal Hours', width: 130, align: 'center',
        info: (
            <>
                <span className="font-semibold">Normal Hours</span> — regular working hours
                inside the standard shift. Shown as decimal hours.
            </>
        ),
    },
    {
        key: 'otHours', label: 'OT Hours', width: 100, align: 'center',
        info: (
            <>
                <span className="font-semibold">OT Hours</span> — total overtime hours
                beyond the standard shift. Shown as decimal hours.
            </>
        ),
    },
    {
        key: 'othoursRate', label: 'OT Hours Rate', width: 120, align: 'center',
        info: (
            <>
                <span className="font-semibold">OT Hours Rate</span> — the multiplier applied
                to OT Hours.
            </>
        ),
    },
    {
        key: 'adjustedOTHours', label: 'Adjusted OT Hours', width: 150, align: 'center',
        info: (
            <>
                <span className="font-semibold">Adjusted OT Hours</span> = OT Hours × OT Hours Rate.
                Shown as decimal hours.
            </>
        ),
    },
    { key: 'action', label: 'Action', width: 100, align: 'center' },
];

const TABLE_MIN_WIDTH = COLUMNS.reduce((s, c) => s + c.width, 0);

export function attendanceStatusLabel(r) {
    switch (r.status) {
        case 'present': return 'Full Day';
        case 'late': return 'Late';
        case 'short-leave':
            return r.statusReason === 'evening' ? 'Evening Short Leave' : 'Morning Short Leave';
        case 'half-day':
            return r.statusReason === 'morning' ? 'Morning Half Day' : 'Evening Half Day';
        case 'missing-punch': return 'Missing Punch';
        case 'leave':
        case 'half-leave': return 'Leave';
        case 'sunday': return 'Sunday';
        case 'holiday': return 'Holiday';
        default: return 'No Record';
    }
}

export default function AttendanceDetails() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const isSummaryTab =
        location.pathname === '/hrm/attendance/details' ||
        location.pathname === '/hrm/attendance/summary';
    const isDetailsTab = location.pathname === '/hrm/attendance/attendance-details';
    const isOverviewTab = location.pathname === '/hrm/attendance/attendance-overview';

    const { rows, leaves, holidays, loading, error } = useSelector(
        (s) => s.attendance || { rows: [], leaves: [], holidays: [], loading: false, error: null }
    );
    const { employees: reduxEmployees, loading: employeesLoading } = useSelector(
        (s) => s.employees || { employees: [], loading: false }
    );
    const leaveTypes = useSelector(selectLeaveTypes);

    const isSearching = useSelector(selectIsSearching);
    const searchError = useSelector(selectSearchError);
    const adjustmentType = useSelector(selectAdjustmentType);
    const adjustedInTime = useSelector(selectAdjustedInTime);
    const adjustedOutTime = useSelector(selectAdjustedOutTime);
    const reason = useSelector(selectReason);
    const submitting = useSelector(selectSubmitting);
    const submitError = useSelector(selectSubmitError);
    const submitSuccess = useSelector(selectSubmitSuccess);

    // ── Local state ──────────────────────────────────────────────
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(() => pad(new Date().getMonth() + 1));
    const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
    const [statusFilter, setStatusFilter] = useState('all');
    const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [modalError, setModalError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const [refreshing, setRefreshing] = useState(false);
    const [sort, setSort] = useState({ key: 'ymd', dir: 'asc' });

    const [profilePicUrl, setProfilePicUrl] = useState(null);
    const [picLoading, setPicLoading] = useState(false);
    const [picFailed, setPicFailed] = useState(false);

    const IMAGE_BASE = tenant.api.employeeImageBaseUrl;

    // ── Scroll plumbing ──────────────────────────────────────────
    const headerRef = useRef(null);
    const tableScrollRef = useRef(null);
    const barRef = useRef(null);
    const syncing = useRef(false);
    const [bar, setBar] = useState({ scrollWidth: 0, hasOverflow: false });

    const heroRef = useRef(null);
    const [heroHeight, setHeroHeight] = useState(0);

    const measureBar = useCallback(() => {
        const el = tableScrollRef.current;
        if (!el) return;
        const next = {
            scrollWidth: el.scrollWidth,
            hasOverflow: el.scrollWidth > el.clientWidth + 1,
        };
        setBar((prev) =>
            prev.scrollWidth === next.scrollWidth && prev.hasOverflow === next.hasOverflow ? prev : next
        );
    }, []);

    useEffect(() => {
        const el = tableScrollRef.current;
        if (!el) return undefined;
        measureBar();
        const ro = new ResizeObserver(measureBar);
        ro.observe(el);
        if (el.firstElementChild) ro.observe(el.firstElementChild);
        window.addEventListener('resize', measureBar);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', measureBar);
        };
    }, [measureBar]);


    const onTableScroll = () => {
        const body = tableScrollRef.current;
        if (headerRef.current && body) headerRef.current.scrollLeft = body.scrollLeft;
        if (syncing.current) { syncing.current = false; return; }
        if (barRef.current && body) {
            syncing.current = true;
            barRef.current.scrollLeft = body.scrollLeft;
        }
    };

    const onBarScroll = () => {
        if (syncing.current) { syncing.current = false; return; }
        if (barRef.current && tableScrollRef.current) {
            syncing.current = true;
            tableScrollRef.current.scrollLeft = barRef.current.scrollLeft;
        }
    };

    // Profile pic
    useEffect(() => {
        if (!selectedEmployee?.employeeId) {
            setProfilePicUrl(null);
            setPicFailed(false);
            setPicLoading(false);
            return;
        }

        let cancelled = false;
        let createdBlobUrl = null;

        (async () => {
            setPicLoading(true);
            setPicFailed(false);
            setProfilePicUrl(null);
            try {
                let target = getEmployeeProfilePicUrl(selectedEmployee);
                if (!target) {
                    target = await dispatch(
                        fetchEmployeeProfilePicUrl(selectedEmployee.employeeId)
                    ).unwrap();
                }
                if (!target) throw new Error('No profile picture');

                const blobUrl = await fetchAuthedBlobUrl(target);
                if (cancelled) {
                    URL.revokeObjectURL(blobUrl);
                    return;
                }
                createdBlobUrl = blobUrl;
                setProfilePicUrl(blobUrl);
            } catch {
                if (!cancelled) setPicFailed(true);
            } finally {
                if (!cancelled) setPicLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
        };
    }, [selectedEmployee, dispatch]);

    // Normalize employees
    useEffect(() => {
        if (!Array.isArray(reduxEmployees) || reduxEmployees.length === 0) return;
        const normalized = reduxEmployees
            .map((emp) => ({
                employeeId: String(emp.HED_EMPLOYEE_ID || emp.EMPLOYEE_ID || emp.employeeId || ''),
                serviceNo: String(emp.HED_SERVICE_NO || emp.SERVICE_NO || emp.serviceNo || emp.HED_EMPLOYEE_ID || ''),
                name: emp.HED_REPORT_NAME ||
                    `${emp.HED_FIRST_NAME || ''} ${emp.HED_LAST_NAME || ''}`.trim() ||
                    `Employee ${emp.HED_SERVICE_NO || ''}`,
                department: emp.HDD_DEPARTMENT_NAME || emp.HED_DEPARTMENT_NAME || '',
                location: emp.HED_LOCATION_NAME || emp.HDL_LOCATION_NAME ||
                    emp.LOCATION_NAME || emp.Location || '',
                designation: emp.HED_DESIGNATION_NAME || '',
                firstName: emp.HED_FIRST_NAME || '',
                lastName: emp.HED_LAST_NAME || '',
                reportName: emp.HED_REPORT_NAME || '',
                category: emp.HED_EMPLOYMENT_CATEGORY || emp.EMPLOYMENT_CATEGORY || '',
                shiftId: emp.HED_SHIFT_ID ?? null,
                shiftCode: emp.HSD_SHIFT_CODE ?? null,
                shiftName: emp.HSD_SHIFT_NAME ?? null,
                shiftStartTime: emp.HSD_START_TIME ?? null,
                shiftEndTime: emp.HSD_END_TIME ?? null,
            }))
            .filter((emp) => emp.employeeId);
        setEmployees(normalized);
    }, [reduxEmployees]);

    useEffect(() => {
        dispatch(fetchAllEmployees());
        dispatch(fetchHolidays());
        dispatch(fetchLeaveTypes());
    }, [dispatch]);

    // ── Null-safe helpers ────────────────────────────────────────
    const hasValidEmployee = !!(selectedEmployee && selectedEmployee.employeeId);

    const handleEmployeeSelect = useCallback((emp) => {
        if (!emp || !emp.employeeId) {
            setSelectedEmployee(null);
            return;
        }
        setSelectedEmployee(emp);
    }, []);

    useEffect(() => {
        if (!selectedYear || !selectedMonth || !hasValidEmployee) return;

        const monthParam = `${selectedYear}-${selectedMonth}`;
        setHasSearched(true);

        dispatch(fetchAttendanceCard({
            serviceNo: String(selectedEmployee.serviceNo),
            month: monthParam,
        }));
        dispatch(fetchApprovedLeaves({
            employeeServiceNo: selectedEmployee.serviceNo || selectedEmployee.employeeId,
        }));
    }, [selectedYear, selectedMonth, hasValidEmployee, selectedEmployee, dispatch]);

    const handleSearch = useCallback(() => {
        if (!hasValidEmployee) {
            setModalError('Please select an employee');
            setTimeout(() => setModalError(''), 3000);
            return;
        }
        const monthParam = `${selectedYear}-${selectedMonth}`;
        setHasSearched(true);
        dispatch(fetchAttendanceCard({
            serviceNo: String(selectedEmployee.serviceNo),
            month: monthParam,
        }));
        dispatch(fetchApprovedLeaves({
            employeeServiceNo: selectedEmployee.serviceNo || selectedEmployee.employeeId,
        }));
    }, [hasValidEmployee, selectedEmployee, selectedYear, selectedMonth, dispatch]);

    const handleReset = useCallback(() => {
        const today = new Date();
        setSelectedYear(String(today.getFullYear()));
        setSelectedMonth(pad(today.getMonth() + 1));
        setSelectedEmployee(null);
        setStatusFilter('all');
        setHasSearched(false);
        setSelectedRecord(null);
        setShowAdjustmentModal(false);
        setModalError('');
        setSort({ key: 'ymd', dir: 'asc' });
        dispatch(resetForm());
    }, [dispatch]);

    const handleRefresh = useCallback(async () => {
        if (refreshing || loading) return;
        setRefreshing(true);
        try {
            const tasks = [
                dispatch(fetchAllEmployees()).unwrap(),
                dispatch(fetchHolidays()).unwrap(),
                dispatch(fetchLeaveTypes()).unwrap(),
            ];
            if (hasValidEmployee) {
                const monthParam = `${selectedYear}-${selectedMonth}`;
                tasks.push(
                    dispatch(fetchAttendanceCard({
                        serviceNo: String(selectedEmployee.serviceNo),
                        month: monthParam,
                    })).unwrap()
                );
                tasks.push(
                    dispatch(fetchApprovedLeaves({
                        employeeServiceNo: selectedEmployee.serviceNo || selectedEmployee.employeeId,
                    })).unwrap()
                );
            }
            await Promise.all(tasks);
        } catch {

        }
        finally { setRefreshing(false); }
    }, [
        dispatch, refreshing, loading, hasValidEmployee,
        selectedEmployee, selectedYear, selectedMonth,
    ]);

    const toggleDateSort = useCallback(() => {
        setSort((s) => ({
            key: 'ymd',
            dir: s.key === 'ymd' && s.dir === 'asc' ? 'desc' : 'asc',
        }));
    }, []);

    const isDateActive = sort.key === 'ymd';

    const handleTabChange = (tab) => {
        if (tab === 'summary') navigate('/hrm/attendance/details');
        else if (tab === 'details') navigate('/hrm/attendance/attendance-details');
        else if (tab === 'overview') navigate('/hrm/attendance/attendance-overview');
    };

    // Leave-type lookup
    const leaveTypeMap = useMemo(() => {
        const map = new Map();
        (leaveTypes || []).forEach((t) => {
            map.set(String(t.id), t.name || t.code || `Type ${t.id}`);
        });
        return map;
    }, [leaveTypes]);

    const resolveLeaveType = useCallback(
        (rawId) => leaveTypeMap.get(String(rawId)) || `Leave (${rawId})`,
        [leaveTypeMap]
    );

    // Employee info header
    const employeeInfo = useMemo(() => {
        if (!hasValidEmployee) return null;
        const firstRow = rows.find((r) =>
            String(r.ServiceNo ?? '').trim() ===
            String(selectedEmployee.serviceNo || selectedEmployee.employeeId).trim()
        ) || rows[0] || {};

        return {
            serviceNo: selectedEmployee.serviceNo || firstRow.ServiceNo || '—',
            name: selectedEmployee.name || firstRow.Name || '—',
            department: selectedEmployee.department || firstRow.Department || '—',
            category: selectedEmployee.category || firstRow.EmploymentCategory || '—',
            location:
                selectedEmployee.location ||
                firstRow.Location ||
                firstRow.LocationName ||
                '—',
        };
    }, [hasValidEmployee, selectedEmployee, rows]);

    useEffect(() => {
        if (!hasSearched || !employeeInfo) {
            setHeroHeight(0);
            return;
        }
        const el = heroRef.current;
        if (!el) return;
        const update = () => setHeroHeight(el.offsetHeight);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        window.addEventListener('resize', update);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', update);
        };
    }, [hasSearched, employeeInfo]);

    // Filter rows to selected employee
    const employeeRows = useMemo(() => {
        if (!hasSearched || !hasValidEmployee) return [];
        const id = String(selectedEmployee.employeeId).trim();
        const sn = String(selectedEmployee.serviceNo || '').trim();
        return rows.filter((r) =>
            String(r.EmployeeID ?? '').trim() === id ||
            String(r.ServiceNo ?? '').trim() === sn
        );
    }, [rows, selectedEmployee, hasSearched, hasValidEmployee]);

    // Filter leaves to selected employee
    const employeeLeaves = useMemo(() => {
        if (!hasSearched || !hasValidEmployee) return [];
        const sn = String(selectedEmployee.serviceNo || selectedEmployee.employeeId || '').trim();
        return leaves.filter((l) => String(l.HLF_EMP_SERVICE_NO ?? '').trim() === sn);
    }, [leaves, selectedEmployee, hasSearched, hasValidEmployee]);

    const holidayMap = useMemo(() => {
        const map = new Map();
        holidays.forEach((h) => {
            if (h?.HAH_IS_ACTIVE === 'False') return;
            const ymd = toYMD(h.HAH_HOLIDAY_DATE);
            if (ymd) map.set(ymd, {
                name: h.HAH_HOLIDAY_NAME || 'Holiday',
                type: h.HAH_HOLIDAY_TYPE || '',
            });
        });
        return map;
    }, [holidays]);

    const attendanceMap = useMemo(() => {
        const map = new Map();
        employeeRows.forEach((r) => {
            const ymd = toYMD(r.AttDate);
            if (ymd) map.set(ymd, r);
        });
        return map;
    }, [employeeRows]);

    const leaveMap = useMemo(() => {
        const map = new Map();
        employeeLeaves.forEach((l) => {
            const from = new Date(l.HLF_FROM_DATE);
            const to = new Date(l.HLF_TO_DATE);
            if (isNaN(from.getTime()) || isNaN(to.getTime())) return;
            const totalDays = parseFloat(l.HLF_TOTAL_DAYS || '0');
            const isHalf = l.HLF_HALF_DAY === 'Y' || totalDays === 0.5;

            const cursor = new Date(from); cursor.setHours(0, 0, 0, 0);
            const end = new Date(to); end.setHours(0, 0, 0, 0);

            while (cursor <= end) {
                const ymd = toYMD(cursor);
                if (ymd) {
                    map.set(ymd, {
                        leaveTypeId: l.HLF_LEAVE_TYPE_ID,
                        leaveTypeName: resolveLeaveType(l.HLF_LEAVE_TYPE_ID),
                        isHalf,
                        days: totalDays,
                        reason: l.HLF_REASON || '',
                        halfDayType: l.HLF_HALF_DAY_TYPE || '',
                    });
                }
                cursor.setDate(cursor.getDate() + 1);
            }
        });
        return map;
    }, [employeeLeaves, resolveLeaveType]);

    const monthDays = useMemo(
        () => buildMonthDays(selectedYear, selectedMonth),
        [selectedYear, selectedMonth]
    );

    const todayStr = useMemo(() => todayYMD(), []);

    const employeeShift = useMemo(
        () => extractShift(selectedEmployee),
        [selectedEmployee]
    );

    /* Raw decimal hours */
    const fmtH = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n) || n <= 0) return '—';
        return `${Number(n.toFixed(2))} h`;
    };

    const calendarRows = useMemo(() => {
        if (!hasValidEmployee) return [];
        return monthDays
            .filter((d) => d.ymd <= todayStr)
            .map((d) => {
                const att = attendanceMap.get(d.ymd);
                const leave = leaveMap.get(d.ymd);
                const holiday = holidayMap.get(d.ymd);
                const isToday = d.ymd === todayStr;

                const inTime = att?.InTime || '';
                const outTime = att?.OutTime || '';
                const totalHours = att?.TotalHours != null
                    ? Number(att.TotalHours)
                    : (inTime && outTime ? computeHours(inTime, outTime) : 0);
                const normalHours = att?.NormalHours != null
                    ? Number(att.NormalHours)
                    : (totalHours > 0 ? Math.min(totalHours, 8) : 0);
                const otHours = att?.OTHours != null
                    ? Number(att.OTHours)
                    : (inTime && outTime ? computeOT(inTime, outTime) : 0);
                const preOT = att?.PreShiftOTHours != null ? Number(att.PreShiftOTHours) : 0;
                const postOT = att?.PostOTHours != null ? Number(att.PostOTHours) : 0;
                const othoursRate = att?.OTHoursRate != null ? Number(att.OTHoursRate) : 0;
                const adjustedOTHours = att?.AdjustedOTHours != null ? Number(att.AdjustedOTHours) : 0;

                const { status, reason: statusReason, missingIn, missingOut } = computeDayStatus({
                    date: d.ymd,
                    attendance: att,
                    leave,
                    holiday,
                    shift: employeeShift,
                });

                return {
                    ...d, att, leave, holiday, isToday,
                    inTime, outTime, totalHours, normalHours, otHours,
                    preOT, postOT, othoursRate, adjustedOTHours,
                    status, statusReason, missingIn, missingOut,
                };
            });
    }, [
        hasValidEmployee, monthDays, attendanceMap, leaveMap, holidayMap,
        todayStr, employeeShift,
    ]);

    const STATUS_MATCHERS = {
        'full-day': (r) => r.status === 'present',
        'late': (r) => r.status === 'late',
        'short-leave-morning': (r) => r.status === 'short-leave' && r.statusReason === 'morning',
        'short-leave-evening': (r) => r.status === 'short-leave' && r.statusReason === 'evening',
        'half-day-morning': (r) => r.status === 'half-day' && r.statusReason === 'morning',
        'half-day-evening': (r) => r.status === 'half-day' && (r.statusReason === 'evening' || r.statusReason === 'early-out'),
        'missing-punch': (r) => r.status === 'missing-punch',
        'leave': (r) => r.status === 'leave' || r.status === 'half-leave',
        // 'sunday': (r) => r.status === 'sunday',
        'holiday': (r) => r.status === 'holiday',
        'no-record': (r) => r.status === 'no-record',
    };

    const filteredCalendarRows = useMemo(() => {
        let rows = calendarRows;

        if (statusFilter !== 'all') {
            const match = STATUS_MATCHERS[statusFilter];
            if (match) rows = rows.filter(match);
        }

        const { key, dir } = sort;
        const mult = dir === 'asc' ? 1 : -1;
        const list = [...rows];
        list.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mult;
            return String(av ?? '').localeCompare(String(bv ?? '')) * mult;
        });
        return list;
    }, [calendarRows, statusFilter, sort]);

    // ── Totals ──────────────────────────────────────────────────
    const totals = useMemo(() => {
        return filteredCalendarRows.reduce(
            (acc, r) => {
                acc.totalHours += Number(r.totalHours) || 0;
                acc.normalHours += Number(r.normalHours) || 0;
                acc.otHours += Number(r.otHours) || 0;
                acc.preOT += Number(r.preOT) || 0;
                acc.postOT += Number(r.postOT) || 0;
                acc.othoursRate += Number(r.othoursRate) || 0;
                acc.adjustedOTHours += Number(r.adjustedOTHours) || 0;
                return acc;
            },
            { totalHours: 0, normalHours: 0, otHours: 0, preOT: 0, postOT: 0, othoursRate: 0, adjustedOTHours: 0 }
        );
    }, [filteredCalendarRows]);

    const handleAdjust = useCallback((record) => {
        setSelectedRecord(record);
        setModalError('');
        if (record.AttDate) dispatch(setAttendanceDate(String(record.AttDate).substring(0, 10)));
        setShowAdjustmentModal(true);
    }, [dispatch]);

    const handleSubmitAdjustment = useCallback(() => {
        const serviceNo = selectedRecord?.ServiceNo;
        const attDate = selectedRecord?.AttDate;
        if (!serviceNo) return setModalError('Service number is required');
        if (!attDate) return setModalError('Attendance date is required');
        if (!reason.trim()) return setModalError('Please enter a reason for the adjustment');

        const ADJ_CODE = {
            'Incorrect IN Time': 'II', 'Incorrect OUT Time': 'IO',
            'Incorrect IN and OUT Times': 'IB', 'Missing IN Time': 'MI',
            'Missing OUT Time': 'MO', 'Missing Full Day': 'MF',
        };
        const needsIn = ['Incorrect IN Time', 'Incorrect IN and OUT Times', 'Missing IN Time', 'Missing Full Day'].includes(adjustmentType);
        const needsOut = ['Incorrect OUT Time', 'Incorrect IN and OUT Times', 'Missing OUT Time', 'Missing Full Day'].includes(adjustmentType);
        if (needsIn && !adjustedInTime) return setModalError('Please enter the adjusted IN time');
        if (needsOut && !adjustedOutTime) return setModalError('Please enter the adjusted OUT time');

        const params = {
            employeeId: serviceNo, attendanceDate: attDate,
            adjType: ADJ_CODE[adjustmentType] || 'L',
            adjReason: reason.trim(), adjStatus: 'P', userId: serviceNo,
        };
        if (needsIn && adjustedInTime) params.inTime = `${attDate} ${adjustedInTime}:00.000`;
        if (needsOut && adjustedOutTime) params.outTime = `${attDate} ${adjustedOutTime}:00.000`;
        dispatch(submitAdjustment(params));
    }, [selectedRecord, reason, adjustmentType, adjustedInTime, adjustedOutTime, dispatch]);

    const handleExport = useCallback(() => {
        if (!filteredCalendarRows.length) return;
        exportAttendanceDetailsToExcel({
            rows: filteredCalendarRows.map((r) => ({
                Date: r.ymd, Day: r.dayName,
                InTime: r.inTime, OutTime: r.outTime,
                TotalHours: r.totalHours.toFixed(2),
                NormalHours: r.normalHours.toFixed(2),
                OTHours: r.otHours.toFixed(2),
                OTHoursRate: r.othoursRate.toFixed(2),
                AdjustedOTHours: r.adjustedOTHours.toFixed(2),
                Status: attendanceStatusLabel(r), Holiday: r.holiday?.name || '', Leave: r.leave?.leaveTypeName || '',
            })),
            filters: {
                Year: selectedYear, Month: selectedMonth,
                Employee: hasValidEmployee
                    ? `${selectedEmployee.name} (${selectedEmployee.serviceNo || selectedEmployee.employeeId})`
                    : '—',
                'Total Records': filteredCalendarRows.length,
            },
        });
    }, [filteredCalendarRows, selectedYear, selectedMonth, selectedEmployee, hasValidEmployee]);

    useEffect(() => {
        if (!submitSuccess) return;
        const t = setTimeout(() => {
            setShowAdjustmentModal(false);
            setSelectedRecord(null);
            setModalError('');
            dispatch(resetForm());
            if (hasValidEmployee) {
                dispatch(fetchAttendanceCard({
                    serviceNo: String(selectedEmployee.serviceNo),
                    month: `${selectedYear}-${selectedMonth}`,
                }));
            }
        }, 2000);
        return () => clearTimeout(t);
    }, [submitSuccess, dispatch, selectedEmployee, selectedYear, selectedMonth, hasValidEmployee]);

    useEffect(() => {
        if (!searchError) return;
        const t = setTimeout(() => dispatch(clearSearchError()), 5000);
        return () => clearTimeout(t);
    }, [searchError, dispatch]);

    const rowClass = (r) => {
        switch (r.status) {
            case 'present': return 'bg-white hover:bg-gray-100';
            case 'late': return 'bg-orange-50 hover:bg-orange-100';
            case 'half-day': return 'bg-indigo-50 hover:bg-indigo-100';
            case 'missing-punch': return 'bg-fuchsia-50 hover:bg-fuchsia-100';
            case 'leave': return 'bg-blue-100 hover:bg-blue-200';
            case 'half-leave': return 'bg-cyan-50 hover:bg-cyan-100';
            case 'short-leave': return 'bg-sky-50 hover:bg-sky-100';
            case 'holiday': return 'bg-yellow-100 hover:bg-yellow-100';
            case 'no-record': return 'bg-slate-50 hover:bg-slate-100';
            default: return 'bg-white hover:bg-slate-50';
        }
    };

    const rowBgSolid = (r) => {
        switch (r.status) {
            case 'present': return 'bg-white';
            case 'late': return 'bg-orange-50';
            case 'half-day': return 'bg-indigo-50';
            case 'missing-punch': return 'bg-fuchsia-50';
            case 'leave': return 'bg-blue-100';
            case 'half-leave': return 'bg-cyan-50';
            case 'short-leave': return 'bg-sky-50';
            case 'holiday': return 'bg-yellow-100';
            case 'no-record': return 'bg-slate-50';
            default: return 'bg-white';
        }
    };

    const statusBadge = (r) => {
        if (r.status === 'present') {
            return (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-400">
                    {attendanceStatusLabel(r)}
                </span>
            );
        }
        if (r.status === 'late') {
            return (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-400">
                    Late
                </span>
            );
        }
        if (r.status === 'short-leave') {
            return (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-400">
                    {attendanceStatusLabel(r)}
                </span>
            );
        }
        if (r.status === 'half-day') {
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-400">
                    {attendanceStatusLabel(r)}
                </span>
            );
        }
        if (r.status === 'missing-punch') {
            return (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-fuchsia-50 text-fuchsia-700 ring-1 ring-inset ring-fuchsia-400">
                    Missing Punch
                </span>
            );
        }
        if (r.status === 'leave') {
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-400">
                    {attendanceStatusLabel(r)}
                </span>
            );
        }
        if (r.status === 'half-leave') {
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-400">
                    {attendanceStatusLabel(r)}
                </span>
            );
        }
        if (r.status === 'holiday') {
            return (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-yellow-100 text-black ring-1 ring-inset ring-yellow-600">
                    {attendanceStatusLabel(r)}
                </span>
            );
        }
        // if (r.status === 'sunday') {
        //     return (
        //         <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-400">
        //             Sunday
        //         </span>
        //     );
        // }
        return (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-300">
                Not attendance recorded
            </span>
        );
    };

    const colGroup = (
        <colgroup>
            {COLUMNS.map((c) => <col key={c.key} style={{ width: c.width }} />)}
        </colgroup>
    );

    return (
        <div className="p-0 md:p-0 space-y-3 bg-slate-50/40 min-h-screen">
            <Breadcrumb
                trail={['eHRM', 'Attendance & Shifts', 'Attendance Details']}
                title="Attendance Details"
                action={
                    <button onClick={handleExport} disabled={!filteredCalendarRows.length || loading}
                        className={BREADCRUMB_EXPORT_BTN}>
                        <FileSpreadsheet size={15} /> Export to Excel
                    </button>
                }
            />

            {/* Tabs */}
            <div className="border-b border-slate-200 bg-white rounded-t-xl px-4 pt-1 shadow-sm">
                <nav className="flex gap-0" aria-label="Tabs">
                    <button onClick={() => handleTabChange('summary')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isSummaryTab ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}>
                        <LayoutGrid size={16} /> Attendance Summary
                    </button>
                    <button onClick={() => handleTabChange('details')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isDetailsTab ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}>
                        <List size={16} /> Attendance Details
                    </button>
                    <button
                        onClick={() => handleTabChange('overview')}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${isOverviewTab
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                            }`}
                    >
                        <Users size={16} /> Attendance Overview
                    </button>
                </nav>
            </div>

            {/* Filters */}
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3.5 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-end gap-3">
                    <div className="flex items-end gap-3 md:w-2/5">
                        <div className="flex-1">
                            <span className={LABEL}>Year</span>
                            <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className={FIELD}>
                                {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        <div className="flex-1">
                            <span className={LABEL}>Month</span>
                            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className={FIELD}>
                                {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>
                        <div className="flex-1">
                            <span className={LABEL}>Show</span>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={FIELD}>
                                {STATUS_FILTERS.map((f) => (
                                    <option key={f.value} value={f.value}>{f.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex items-end gap-2 md:flex-1 md:justify-end">
                        <div className="flex-1 min-w-[220px] max-w-md">
                            <span className={LABEL}>Search Employee</span>
                            <EmployeeSearch
                                value={selectedEmployee}
                                onSelect={handleEmployeeSelect}
                                employees={employees}
                                loading={employeesLoading || isSearching}
                                showAllOption={false}
                            />
                        </div>
                        <button onClick={handleSearch} disabled={loading || !hasValidEmployee}
                            className="flex items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 whitespace-nowrap shadow-sm">
                            <Search size={14} /> {loading ? 'Loading...' : 'Search'}
                        </button>
                        <button onClick={handleReset}
                            className="flex items-center gap-2 h-9 px-3 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-200 transition-colors whitespace-nowrap">
                            <RotateCcw size={14} /> Reset
                        </button>

                        {/* ─── NEW: Refresh ─── */}
                        <button
                            onClick={handleRefresh}
                            disabled={refreshing || loading}
                            title="Refresh data"
                            className="flex items-center gap-2 h-9 px-3 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-200 transition-colors whitespace-nowrap disabled:opacity-50"
                        >
                            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Alerts */}
            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
            {searchError && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg text-sm flex items-center justify-between">
                    <span>{searchError}</span>
                    <button onClick={() => dispatch(clearSearchError())}><X size={14} /></button>
                </div>
            )}
            {submitError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center justify-between">
                    <span>{submitError}</span>
                    <button onClick={() => dispatch(clearSubmitError())}><X size={14} /></button>
                </div>
            )}
            {submitSuccess && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <Check size={16} /> Adjustment submitted successfully!
                </div>
            )}

            {/* ══ Employee Info + Attendance Calendar ══ */}
            {hasSearched && employeeInfo && (
                <div className="rounded-2xl border border-slate-200 shadow-sm bg-white overflow-clip">

                    {/* ───────── Sticky Hero + Title Bar ───────── */}
                    <div
                        ref={heroRef}
                        className="sticky z-40 bg-white"
                        style={{ top: STICKY_TOP }}
                    >
                        {/* ───────── Employee Hero ───────── */}
                        <div className="relative bg-gradient-to-r from-primary-700 via-primary-700 to-primary-700 px-5 py-5 overflow-hidden">
                            <div className="pointer-events-none absolute -top-16 -right-16 w-52 h-52 rounded-full bg-white/10 blur-3xl" />

                            <div className="relative flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                                <div className="flex items-center gap-3.5 min-w-0 lg:w-72 shrink-0">
                                    <div className="w-16 h-16 rounded-2xl ring-1 ring-white/40 shadow-md shrink-0 overflow-hidden bg-gradient-to-br from-white/30 to-white/10 backdrop-blur-sm flex items-center justify-center">
                                        {picLoading && (
                                            <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                        )}
                                        {!picLoading && profilePicUrl && !picFailed && (
                                            <img
                                                src={profilePicUrl}
                                                alt={employeeInfo.name || 'Employee'}
                                                className="w-full h-full object-cover"
                                                onError={() => setPicFailed(true)}
                                            />
                                        )}
                                        {!picLoading && (!profilePicUrl || picFailed) && (
                                            <span className="text-white text-base font-bold">
                                                {(employeeInfo.name || '?')
                                                    .trim()
                                                    .split(/\s+/)
                                                    .slice(0, 2)
                                                    .map((p) => p[0]?.toUpperCase())
                                                    .join('')}
                                            </span>
                                        )}
                                    </div>

                                    <div className="min-w-0">
                                        <p className="text-[16px] font-semibold text-white truncate leading-tight">
                                            {employeeInfo.name || '—'}
                                        </p>
                                        <span className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-800 bg-white/95 rounded-full px-2.5 py-0.5 shadow-sm">
                                            <BadgeCheck size={12} className="text-emerald-600" />
                                            {employeeInfo.serviceNo || '—'}
                                        </span>
                                    </div>
                                </div>

                                <div className="hidden lg:block w-px h-12 bg-white/25" />

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 flex-1 min-w-0">
                                    <div className="group flex items-center gap-3 min-w-0 h-full rounded-xl px-3 py-2.5 bg-white/10 hover:bg-white/15 ring-1 ring-white/20 hover:ring-white/35 backdrop-blur-sm transition-all">
                                        <div className="w-9 h-9 rounded-lg bg-white/20 ring-1 ring-white/40 flex items-center justify-center shadow-sm shrink-0 backdrop-blur-sm">
                                            <Building2 size={15} className="text-white" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[12px] uppercase tracking-wider font-semibold text-emerald-50/80 leading-none">
                                                Department
                                            </p>
                                            <p className="text-[13.5px] font-semibold text-white truncate mt-1.5" title={employeeInfo.department || ''}>
                                                {employeeInfo.department || '—'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="group flex items-center gap-3 min-w-0 h-full rounded-xl px-3 py-2.5 bg-white/10 hover:bg-white/15 ring-1 ring-white/20 hover:ring-white/35 backdrop-blur-sm transition-all">
                                        <div className="w-9 h-9 rounded-lg bg-white/20 ring-1 ring-white/40 flex items-center justify-center shadow-sm shrink-0 backdrop-blur-sm">
                                            <BriefcaseBusiness size={15} className="text-white" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[12px] uppercase tracking-wider font-semibold text-emerald-50/80 leading-none">
                                                Category
                                            </p>
                                            <p className="text-[13.5px] font-semibold text-white truncate mt-1.5" title={employeeInfo.category || ''}>
                                                {employeeInfo.category || '—'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="group flex items-center gap-3 min-w-0 h-full rounded-xl px-3 py-2.5 bg-white/10 hover:bg-white/15 ring-1 ring-white/20 hover:ring-white/35 backdrop-blur-sm transition-all">
                                        <div className="w-9 h-9 rounded-lg bg-white/20 ring-1 ring-white/40 flex items-center justify-center shadow-sm shrink-0 backdrop-blur-sm">
                                            <MapPin size={15} className="text-white" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[12px] uppercase tracking-wider font-semibold text-emerald-50/80 leading-none">
                                                Location
                                            </p>
                                            <p className="text-[13.5px] font-semibold text-white truncate mt-1.5" title={employeeInfo.location || ''}>
                                                {employeeInfo.location || '—'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="group flex items-center gap-3 min-w-0 h-full rounded-xl px-3 py-2.5 bg-white/10 hover:bg-white/15 ring-1 ring-white/20 hover:ring-white/35 backdrop-blur-sm transition-all">
                                        <div className="w-9 h-9 rounded-lg bg-white/20 ring-1 ring-white/40 flex items-center justify-center shadow-sm shrink-0 backdrop-blur-sm">
                                            <Clock size={15} className="text-white" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[12px] uppercase tracking-wider font-semibold text-emerald-50/80 leading-none">
                                                Shift
                                            </p>
                                            <p
                                                className="text-[13.5px] font-semibold text-white truncate mt-1.5 tabular-nums"
                                                title={
                                                    employeeShift?.name
                                                        ? `${employeeShift.name} | ${(employeeShift.startTime || '').slice(0, 5)} - ${(employeeShift.endTime || '').slice(0, 5)}`
                                                        : 'No shift assigned'
                                                }
                                            >
                                                {employeeShift?.name && employeeShift?.startTime && employeeShift?.endTime
                                                    ? `${employeeShift.name}  |  ${employeeShift.startTime.slice(0, 5)} - ${employeeShift.endTime.slice(0, 5)}`
                                                    : 'No shift assigned'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ───────── Section Title Bar ───────── */}
                        <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-4 bg-white">
                            <div className="flex items-center gap-2 text-slate-800">
                                <CalendarDays size={16} className="text-primary-600" />
                                <h3 className="text-sm font-semibold">Attendance Details</h3>
                                <span className="text-xs text-slate-400 font-normal">
                                    · {filteredCalendarRows.length} of {calendarRows.length} days
                                </span>
                            </div>

                            {/* ─── Date ASC/DESC ─── */}
                            <button
                                type="button"
                                onClick={toggleDateSort}
                                title={
                                    isDateActive && sort.dir === 'asc'
                                        ? 'Sorted oldest → newest. Click for newest → oldest.'
                                        : 'Sorted newest → oldest. Click for oldest → newest.'
                                }
                                className={`ml-auto inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-all active:scale-[0.98] ${isDateActive
                                    ? 'border-primary-200 bg-primary-50 text-primary-700 shadow-sm hover:bg-primary-100'
                                    : 'border-slate-200 bg-white text-slate-600 shadow-sm hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900'
                                    }`}
                            >
                                {isDateActive ? (
                                    sort.dir === 'asc'
                                        ? <ArrowUp size={13} />
                                        : <ArrowDown size={13} />
                                ) : (
                                    <ArrowUpDown size={13} className="text-slate-400" />
                                )}
                                <span className="tracking-tight">Date</span>
                                {isDateActive && (
                                    <span className="ml-0.5 text-[10px] font-bold tracking-widest opacity-60">
                                        {sort.dir === 'asc' ? 'ASC' : 'DESC'}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>


                    {/* ───────── Sticky Header Table ───────── */}
                    <div
                        ref={headerRef}
                        className="sticky z-30 overflow-hidden bg-slate-100"
                        style={{ top: STICKY_TOP + heroHeight }}
                    >
                        <table
                            className="text-xs border-separate border-spacing-0"
                            style={{ minWidth: TABLE_MIN_WIDTH, width: '100%', tableLayout: 'fixed' }}
                        >
                            {colGroup}
                            <thead>
                                <tr className="text-slate-500">
                                    {COLUMNS.map((c) => (
                                        <th
                                            key={c.key}
                                            style={{
                                                width: c.width,
                                                ...(c.sticky ? { position: 'sticky', left: c.left, zIndex: 20 } : {}),
                                            }}
                                            className={`px-3 py-2.5 font-semibold bg-slate-100 border-b border-slate-200
                                    ${c.align === 'center' ? 'text-center' : c.align === 'right' ? 'text-right' : 'text-left'}`}
                                        >
                                            <span className="inline-flex items-center justify-center gap-1">
                                                {c.label}
                                                {c.info && <InfoTip>{c.info}</InfoTip>}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                        </table>
                    </div>

                    {/* ───────── Body Table ───────── */}
                    <div
                        ref={tableScrollRef}
                        onScroll={onTableScroll}
                        className="relative overflow-x-auto [&::-webkit-scrollbar]:hidden"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        <table
                            className="text-xs border-separate border-spacing-0"
                            style={{ minWidth: TABLE_MIN_WIDTH, width: '100%', tableLayout: 'fixed' }}
                        >
                            {colGroup}
                            <tbody>
                                {filteredCalendarRows.map((r) => (
                                    <tr key={r.ymd} className={`group transition-colors ${rowClass(r)}`}>
                                        {/* Date — sticky */}
                                        <td
                                            style={{ position: 'sticky', left: 0, zIndex: 10 }}
                                            className={`px-3 py-2 text-slate-700 border-b border-slate-100 font-medium tabular-nums transition-colors ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            {r.ymd}
                                        </td>


                                        {/* Day — sticky */}
                                        <td
                                            style={{ position: 'sticky', left: 130, zIndex: 10 }}
                                            className={`px-3 py-2 border-b border-slate-100 transition-colors ${rowBgSolid(r)} ${rowBgHover(r)}`}
                                        >
                                            <span className={`font-semibold ${r.isSunday ? 'text-rose-600' : 'text-slate-600'}`}>
                                                {r.dayName}
                                            </span>
                                        </td>

                                        <td className="px-2 py-2 text-center border-b border-slate-100">
                                            {statusBadge(r)}
                                        </td>

                                        <td className="px-3 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums">
                                            {r.inTime || '—'}
                                        </td>

                                        <td className="px-3 py-2 text-center text-slate-700 border-b border-slate-100 tabular-nums">
                                            {r.outTime || '—'}
                                        </td>

                                        {/* Total Hours */}
                                        <td className="px-3 py-2 text-center tabular-nums text-slate-700 border-b border-slate-100">
                                            {fmtH(r.totalHours)}
                                        </td>

                                        {/* Normal Hours */}
                                        <td className="px-3 py-2 text-center tabular-nums text-slate-700 border-b border-slate-100">
                                            {fmtH(r.normalHours)}
                                        </td>

                                        {/* OT Hours */}
                                        <td className="px-3 py-2 text-center tabular-nums border-b border-slate-100">
                                            {r.otHours > 0
                                                ? <span className="font-semibold text-emerald-700">{fmtH(r.otHours)}</span>
                                                : <span className="text-slate-400">—</span>
                                            }
                                        </td>

                                        {/* OT Hours Rate */}
                                        <td className="px-3 py-2 text-center tabular-nums text-slate-700 border-b border-slate-100">
                                            {r.othoursRate > 0 ? r.othoursRate.toFixed(2) : '—'}
                                        </td>

                                        {/* Adjusted OT Hours */}
                                        <td className="px-3 py-2 text-center tabular-nums text-slate-700 border-b border-slate-100">
                                            {fmtH(r.adjustedOTHours)}
                                        </td>

                                        {/* Action */}
                                        <td className="px-3 py-2 text-center border-b border-slate-100">
                                            {r.status !== 'leave' && (
                                                <button
                                                    onClick={() => handleAdjust(r.att || {
                                                        ServiceNo: employeeInfo?.serviceNo,
                                                        AttDate: r.ymd,
                                                        Name: employeeInfo?.name,
                                                        Department: employeeInfo?.department,
                                                        Location: employeeInfo?.location,
                                                    })}
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded-md transition-colors">
                                                    <Edit size={12} /> Adjust
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {filteredCalendarRows.length === 0 && (
                                    <tr>
                                        <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-slate-400">
                                            {!hasValidEmployee
                                                ? 'Please select an employee to view attendance.'
                                                : hasSearched
                                                    ? (statusFilter === 'all'
                                                        ? 'No data for this month.'
                                                        : `No ${STATUS_FILTERS.find(f => f.value === statusFilter)?.label || ''} records this month.`)
                                                    : 'Select filters and click Search.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>

                            {/* ───────── Totals ───────── */}
                            {filteredCalendarRows.length > 0 && (
                                <tfoot>
                                    <tr className="bg-slate-100 font-semibold text-slate-800">
                                        <td
                                            style={{ position: 'sticky', left: 0, zIndex: 10 }}
                                            className="bg-slate-100 px-3 py-2.5 border-t border-slate-200"
                                        />
                                        <td
                                            style={{ position: 'sticky', left: 130, zIndex: 10 }}
                                            className="bg-slate-100 px-3 py-2.5 border-t border-slate-200"
                                        />
                                        <td
                                            colSpan={3}
                                            className="bg-slate-100 px-3 py-2.5 border-t border-slate-200 text-right text-[11px] uppercase tracking-wide text-slate-600"
                                        />
                                        <td className="bg-slate-100 px-3 py-2.5 text-center tabular-nums border-t border-slate-200">
                                            {fmtH(totals.totalHours)}
                                        </td>
                                        <td className="bg-slate-100 px-3 py-2.5 text-center tabular-nums border-t border-slate-200">
                                            {fmtH(totals.normalHours)}
                                        </td>
                                        <td className="bg-slate-100 px-3 py-2.5 text-center tabular-nums border-t border-slate-200">
                                            {fmtH(totals.otHours)}
                                        </td>
                                        <td className="bg-slate-100 px-3 py-2.5 border-t border-slate-200" />
                                        <td className="bg-slate-100 px-3 py-2.5 text-center tabular-nums border-t border-slate-200">
                                            {fmtH(totals.adjustedOTHours)}
                                        </td>
                                        <td className="bg-slate-100 px-3 py-2.5 border-t border-slate-200" />
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>

                    {/* ───────── Sticky horizontal scrollbar ───────── */}
                    <div
                        ref={barRef}
                        onScroll={onBarScroll}
                        className="sticky z-30 overflow-x-auto overflow-y-hidden bg-white/95 border-t border-gray-200 rounded-b-xl
                [&::-webkit-scrollbar]:h-2
                [&::-webkit-scrollbar-track]:bg-green-50
                [&::-webkit-scrollbar-thumb]:bg-green-300
                [&::-webkit-scrollbar-thumb]:rounded-full
                [&::-webkit-scrollbar-thumb:hover]:bg-green-400"
                        style={{
                            bottom: STICKY_BOTTOM,
                            height: 20,
                            display: bar.hasOverflow && filteredCalendarRows.length > 0 ? 'block' : 'none',
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#86efac #f0fdf4',
                        }}
                        aria-hidden="true"
                    >
                        <div style={{ width: bar.scrollWidth, height: 1 }} />
                    </div>
                </div>
            )}

            {/* Adjustment Modal */}
            {showAdjustmentModal && selectedRecord && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
                            <h3 className="text-lg font-semibold text-slate-900">Attendance Adjustment</h3>
                            <button onClick={() => { setShowAdjustmentModal(false); setSelectedRecord(null); setModalError(''); dispatch(resetForm()); }}
                                className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg">
                                <div><p className="text-xs text-slate-500">Service No</p><p className="text-sm font-medium text-slate-900">{selectedRecord.ServiceNo || '—'}</p></div>
                                <div><p className="text-xs text-slate-500">Name</p><p className="text-sm font-medium text-slate-900">{selectedRecord.Name || '—'}</p></div>
                                <div><p className="text-xs text-slate-500">Department</p><p className="text-sm text-slate-700">{selectedRecord.Department || '—'}</p></div>
                                <div><p className="text-xs text-slate-500">Location</p><p className="text-sm text-slate-700">{selectedRecord.Location || '—'}</p></div>
                                <div><p className="text-xs text-slate-500">Designation</p><p className="text-sm text-slate-700">{selectedRecord.Designation || '—'}</p></div>
                                <div><p className="text-xs text-slate-500">Date</p><p className="text-sm text-slate-700">{selectedRecord.AttDate || '—'}</p></div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="text-center p-3 bg-slate-50 rounded-lg">
                                    <p className="text-xs text-slate-500">Current IN Time</p>
                                    <p className="text-lg font-semibold text-slate-900">{selectedRecord.InTime || '—'}</p>
                                </div>
                                <div className="text-center p-3 bg-slate-50 rounded-lg">
                                    <p className="text-xs text-slate-500">Current OUT Time</p>
                                    <p className="text-lg font-semibold text-slate-900">{selectedRecord.OutTime || '—'}</p>
                                </div>
                            </div>
                            {modalError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs flex items-center gap-2">
                                    <AlertCircle size={14} /> {modalError}
                                </div>
                            )}
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-700">Adjustment Type</label>
                                <div className="relative">
                                    <select value={adjustmentType} onChange={(e) => dispatch(setAdjustmentType(e.target.value))}
                                        className="w-full appearance-none rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100">
                                        {ADJUSTMENT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                                    </select>
                                    <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                </div>
                            </div>
                            {['Incorrect IN Time', 'Incorrect IN and OUT Times', 'Missing IN Time', 'Missing Full Day'].includes(adjustmentType) && (
                                <TimePicker24h value={adjustedInTime} onChange={(v) => dispatch(setAdjustedInTime(v))} label="Adjusted IN Time" required />
                            )}
                            {['Incorrect OUT Time', 'Incorrect IN and OUT Times', 'Missing OUT Time', 'Missing Full Day'].includes(adjustmentType) && (
                                <TimePicker24h value={adjustedOutTime} onChange={(v) => dispatch(setAdjustedOutTime(v))} label="Adjusted OUT Time" required />
                            )}
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-slate-700">Reason for Adjustment</label>
                                <textarea value={reason} onChange={(e) => dispatch(setReason(e.target.value))} rows={3}
                                    placeholder="Explain why this record needs to be corrected..."
                                    className="w-full resize-none rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
                            </div>
                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                                <button onClick={() => { setShowAdjustmentModal(false); setSelectedRecord(null); setModalError(''); dispatch(resetForm()); }}
                                    className="px-4 py-2 text-xs font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors">Cancel</button>
                                <button onClick={handleSubmitAdjustment} disabled={submitting}
                                    className="px-4 py-2 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50">
                                    {submitting ? 'Submitting...' : 'Submit Adjustment'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}